import React, { PureComponent } from 'react';
import {
  WithStyles,
  createStyles,
  withStyles,
  Theme,
} from '@lifeomic/chroma-react/styles';
import { Check, ChevronDown } from '@lifeomic/chromicons';
import clsx from 'clsx';
import { connect, FormikProps, getIn } from 'formik';
import SurveyItem from '../../util/SurveyItem';
import { SurveyItemComponent } from '../SurveyItem';
import ConfirmStrip from './ConfirmStrip';
import { Text } from '@lifeomic/chroma-react/components/Text';
import getSurveyItemTestId from '../../util/getSurveyItemTestId';
import { getTestProps } from '../../../../util/testProps';
import withCheckForEnablement, {
  WithCheckForEnablementInjectedProps,
} from '../hoc/withCheckForEnablement';
import withIsMobile, {
  WithIsMobileInjectedProps,
} from '../../../../hoc/withIsMobile';
import {
  withSurveyResponseContext,
  withSurveyContext,
  withRepeatingGroupInstance,
  WithRepeatingGroupInstanceInjectedProps,
  WithSurveyContextInjectedProps,
  WithSurveyResponseContextInjectedProps,
  RegisterCollapsibleSectionContext,
} from '../Contexts';

export const testIds = {
  sectionToggle: (linkId: string) =>
    getSurveyItemTestId(linkId, 'section-toggle'),
  sectionContent: (linkId: string) =>
    getSurveyItemTestId(linkId, 'section-content'),
  groupLine: (linkId: string, lineIndex: number) =>
    getSurveyItemTestId(linkId, `line-${lineIndex}`),
  sectionStatus: (linkId: string) =>
    getSurveyItemTestId(linkId, 'section-status'),
  sectionComplete: (linkId: string) =>
    getSurveyItemTestId(linkId, 'section-complete'),
};

const styles = (theme: Theme) =>
  createStyles({
    root: {
      width: '100%',
      marginTop: theme.spacing(6),
    },
    subItemRoot: {
      // No explicit width: `width: 100%` plus horizontal margins makes the
      // outer box wider than the section, pushing table right edges (e.g.
      // matrix YES/NO columns) past the visible area. Block auto-width
      // subtracts the margins so content always fits the section.
      margin: theme.spacing(2),
    },
    collapsibleRoot: {
      width: '100%',
      marginTop: theme.spacing(4),
    },
    sectionHeader: {
      width: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: theme.spacing(1.5, 2),
      background: theme.palette.grey[100],
      border: 'none',
      borderBottom: `2px solid ${theme.palette.grey[300]}`,
      borderRadius: theme.pxToRem(4),
      cursor: 'pointer',
      textAlign: 'left',
    },
    sectionChevron: {
      color: theme.palette.text.secondary,
      transition: 'transform 150ms ease',
      flexShrink: 0,
    },
    sectionChevronCollapsed: {
      transform: 'rotate(-90deg)',
    },
    sectionStatusContainer: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1),
      color: theme.palette.text.secondary,
      flexShrink: 0,
    },
    sectionStatus: {
      color: theme.palette.text.secondary,
    },
    // Collapsed sections stay mounted (display:none) so their Formik fields
    // remain registered and validation still runs on submit.
    sectionItemsCollapsed: {
      display: 'none',
    },
    // Page-level confirm strips are hoisted into SurveyLayout sticky chrome;
    // this wrapper is the strip + hidden child fields for autopopulate.
    confirmRoot: {
      width: '100%',
    },
    // Unlabeled groups used as table-row-detail wrappers (e.g. pairing
    // grip kg + rank on one line) should not add the section's 48px top
    // margin or inner inset; the detail well already provides that.
    nestedDetailRoot: {
      width: '100%',
    },
    // Mirrors PaginatedSurveyForm's formLine so same-line items pair up
    // inside groups exactly like they do as direct page children.
    formLine: {
      width: '100%',
      display: 'flex',
      // bottom-align so inputs stay on one line when a label wraps
      alignItems: 'flex-end',
      gap: theme.spacing(2),
    },
    // Mobile stacking is CSS-only: the line wrappers always render so the
    // element tree keeps the same shape across the breakpoint. Otherwise
    // resizing across it would remount every child and wipe local UI state
    // (e.g. this component's isCollapsed).
    formLineStacked: {
      flexDirection: 'column',
      alignItems: 'stretch',
    },
  });

export interface GroupOwnProps {
  surveyItem: SurveyItem;
}

// surveyResponse injection ensures PureComponent re-renders when repeating
// group instances change without remounting.
export type GroupProps = WithStyles<typeof styles> &
  WithCheckForEnablementInjectedProps &
  WithIsMobileInjectedProps &
  WithSurveyContextInjectedProps & {
    formik: FormikProps<any>;
  } & WithRepeatingGroupInstanceInjectedProps &
  WithSurveyResponseContextInjectedProps &
  GroupOwnProps;

interface GroupState {
  isCollapsed: boolean;
}

class Group extends PureComponent<GroupProps, GroupState> {
  static contextType = RegisterCollapsibleSectionContext;
  declare context: React.ContextType<typeof RegisterCollapsibleSectionContext>;

  state: GroupState = {
    isCollapsed: this.props.surveyItem.startsCollapsed,
  };

  componentDidMount() {
    if (this.props.surveyItem.isCollapsibleSection) {
      this.context(this.getSectionKey(), this.expandSection);
    }
  }

  componentWillUnmount() {
    if (this.props.surveyItem.isCollapsibleSection) {
      this.context(this.getSectionKey(), null);
    }
  }

  getSectionKey = () =>
    this.props.survey?.getSectionCompletionKey(
      this.props.surveyItem.link,
      this.props.repeatingGroupInstance?.ancestors,
    ) || this.props.surveyItem.link;

  expandSection = () => {
    if (this.state.isCollapsed) {
      this.setState({ isCollapsed: false });
    }
  };

  handleToggle = () => {
    this.setState(prevState => ({ isCollapsed: !prevState.isCollapsed }));
  };

  getHeaderText = () => {
    const {
      surveyItem: { prefix, text },
    } = this.props;

    if (prefix) {
      return `(${prefix}) ${text}`;
    }
    return text || '';
  };

  renderSubItems = () => {
    const { surveyItem, classes, isMobile } = this.props;

    // Honor the same-line extension inside groups: without this, nested
    // section groups stack every item (e.g. Height feet/inches) one per row.
    // The wrappers render on mobile too (stacked via CSS) so that crossing
    // the breakpoint never changes the tree shape and remounts children.
    return surveyItem.subItemsGroupedByDisplayLines.map(
      (lineSurveyItems, index) => (
        <div
          className={clsx(
            classes.formLine,
            isMobile && classes.formLineStacked,
          )}
          key={lineSurveyItems[0]?.link ?? index}
          {...getTestProps(testIds.groupLine(surveyItem.link, index))}
        >
          {lineSurveyItems.filter(Boolean).map(lineSurveyItem => (
            <SurveyItemComponent
              key={lineSurveyItem.link}
              surveyItem={lineSurveyItem}
            />
          ))}
        </div>
      ),
    );
  };

  renderCollapsibleSection = () => {
    const { surveyItem, classes, survey, surveyResponse, formik } = this.props;
    const { isCollapsed } = this.state;
    const contentId = `${surveyItem.link}-section-content`;
    const completion = survey?.showsSectionCompletionStatus()
      ? survey
          .getSectionCompletionStatuses(surveyResponse)
          .get(this.getSectionKey())
      : undefined;
    const errorFields =
      completion && formik.submitCount > 0
        ? completion.enabledFieldNames.filter(fieldName =>
            Boolean(getIn(formik.errors, fieldName)),
          )
        : [];
    const extraErrorCount =
      completion?.requiredRemainingFieldNames == null
        ? 0
        : errorFields.filter(
            fieldName =>
              !completion.requiredRemainingFieldNames.includes(fieldName),
          ).length;
    const remainingCount =
      (completion?.requiredRemaining || 0) + extraErrorCount;
    const showRemaining =
      !!completion &&
      remainingCount > 0 &&
      (completion.requiredAnswered > 0 || errorFields.length > 0);
    const showDone =
      !!completion && completion.requiredTotal > 0 && remainingCount === 0;
    const statusLabel = showRemaining
      ? `${remainingCount} required remaining`
      : showDone
      ? 'all required questions answered'
      : '';
    const ariaLabel = [
      this.getHeaderText(),
      statusLabel,
      isCollapsed ? 'collapsed' : 'expanded',
    ]
      .filter(Boolean)
      .join(', ');

    return (
      <div className={classes.collapsibleRoot}>
        {/* type="button" is required: the survey renders inside a <form>,
            and a default (submit) button would Next/Complete the survey */}
        <button
          type="button"
          className={classes.sectionHeader}
          onClick={this.handleToggle}
          aria-expanded={!isCollapsed}
          aria-controls={contentId}
          aria-label={ariaLabel}
          {...getTestProps(testIds.sectionToggle(surveyItem.link))}
        >
          <Text size="body" weight="bold">
            {this.getHeaderText()}
          </Text>
          <span className={classes.sectionStatusContainer}>
            {showRemaining ? (
              <Text
                size="caption"
                className={classes.sectionStatus}
                {...getTestProps(testIds.sectionStatus(surveyItem.link))}
              >
                {remainingCount} left
              </Text>
            ) : null}
            {showDone ? (
              <Check
                aria-hidden
                {...getTestProps(testIds.sectionComplete(surveyItem.link))}
              />
            ) : null}
            <ChevronDown
              aria-hidden
              className={clsx(
                classes.sectionChevron,
                isCollapsed && classes.sectionChevronCollapsed,
              )}
            />
          </span>
        </button>
        <div
          id={contentId}
          className={clsx(
            classes.subItemRoot,
            isCollapsed && classes.sectionItemsCollapsed,
          )}
          {...getTestProps(testIds.sectionContent(surveyItem.link))}
        >
          {this.renderSubItems()}
        </div>
      </div>
    );
  };

  renderConfirmStrip = () => {
    const { surveyItem, classes } = this.props;

    return (
      <div className={classes.confirmRoot}>
        <ConfirmStrip surveyItem={surveyItem} />
        {/* Hidden so autopopulate still writes Formik/response values for
            the note template, without showing intake fields. */}
        <div className={classes.sectionItemsCollapsed} aria-hidden>
          {this.renderSubItems()}
        </div>
      </div>
    );
  };

  render() {
    const { surveyItem, classes, isEnabled } = this.props;

    if (!isEnabled) return null;

    if (surveyItem.isConfirmStrip) {
      return this.renderConfirmStrip();
    }

    if (surveyItem.isCollapsibleSection) {
      return this.renderCollapsibleSection();
    }

    const headerText = this.getHeaderText();

    if (surveyItem.isTableRowDetail && !headerText) {
      return (
        <div className={classes.nestedDetailRoot}>{this.renderSubItems()}</div>
      );
    }

    return (
      <div className={classes.root}>
        {headerText ? <Text size="headline">{headerText}</Text> : null}
        <div className={classes.subItemRoot}>{this.renderSubItems()}</div>
      </div>
    );
  }
}

export default withIsMobile(
  withStyles(styles)(
    withCheckForEnablement(
      withSurveyContext(
        withSurveyResponseContext(withRepeatingGroupInstance(connect(Group))),
      ),
    ),
  ),
);
